import React from 'react';
import ReactDOM, { unmountComponentAtNode } from 'react-dom';
import { element, event, getElementByTag } from '../../utils/easy';

import './dialog.scss';

export type NullableHTMLElement = HTMLElement | null;

export interface DialogOptions {
  rtl?: boolean;
  title?: string;
  size?: 'large' | 'small';
  message?: string | JSX.Element | any;
  backdrop?: boolean | null;
  showTitle?: boolean;
  resolveOnClose?: boolean;
  titleFontFamily?: string;
  backdropClick?: () => void;
  onResolve?: (data: any) => void;
  onClose?: (result: any) => void;
}

export default class Dialog {

  public static DEFAULT_RTL: boolean = false;
  public static DEFAULT_RESOLVE_ON_CLOSE: boolean = false;

  public static get STATUS_OPEN(): string { return 'open' };
  public static get STATUS_CLOSE(): string { return 'close' };
  public static get STATUS_OPENING(): string { return 'opening' };

  private status = '';
  private options: DialogOptions;

  private html = getElementByTag('html');
  private body = getElementByTag('body');
  private card: NullableHTMLElement = null;
  private toolkitWrapper: NullableHTMLElement = null;

  constructor(options: DialogOptions) {
    if (!options) options = {};
    if (options.message === undefined) options.message = 'Sample Dialog Text';
    if (options.title === undefined) options.title = '';
    if (options.backdrop === undefined) options.backdrop = true;
    if (options.rtl === undefined) options.rtl = Dialog.DEFAULT_RTL;
    if (options.size === undefined) options.size = 'large';
    if (options.resolveOnClose === undefined) options.resolveOnClose = Dialog.DEFAULT_RESOLVE_ON_CLOSE;
    if (options.showTitle === undefined) options.showTitle = true;
    if (options.titleFontFamily === undefined) options.titleFontFamily = 'inherit';
    this.options = options;
  }

  public show(props: any = {}) {
    this.setStatus(Dialog.STATUS_OPENING);

    if (typeof props !== 'object') props = {};
    props = { ...props, ...{ dialogInstance: this } };

    const options = this.options;
    const backdropOption = options.backdrop;

    const responsive = this.getResponsiveClasses();

    const toolkitWrapper = element('div', {
      id: 'toolkit-dialog-wrapper',
      class: options.rtl ? ['rtl'] : []
    });
    const card = element('div', { class: ['dialog-card', ...responsive] });
    const content = element('div', { class: 'dialog-content' });
    this.card = card;
    this.toolkitWrapper = toolkitWrapper;

    this.hiddenPageScroll();

    let backdrop: HTMLElement = null;
    if (backdropOption || backdropOption === null) {
      backdrop = element('div', { class: 'toolkit-dialog-backdrop' });
      toolkitWrapper.appendChild(backdrop);
      if (backdropOption) {
        event(backdrop, 'click', this.close);
      }
      if (options.backdropClick) {
        event(backdrop, 'click', options.backdropClick);
      }
    }

    if (options.showTitle) {
      const title = element('div', { class: 'dialog-header' });
      title.innerHTML = `
        <span class="dialog-close-button"><i class="fal fa-times"></i></span>
        <span class="dialog-title" style="font-family: ${options.titleFontFamily}">${options.title}</span>
      `;
      card.appendChild(title);
      card.appendChild(element('hr', { class: 'header-divider' }))
    }
    card.appendChild(content);
    toolkitWrapper.appendChild(card);
    this.body.appendChild(toolkitWrapper);

    // isReactClassComponent(options.message)
    if (typeof options.message === 'function') {
      options.message = React.createElement<any, any, any>(options.message, props, null);
    }

    ReactDOM.render(options.message, content, () => {
      setTimeout(() => {
        toolkitWrapper.style.opacity = '1';
        const buttons = document.querySelectorAll('.dialog-close-button');
        if (buttons.length > 0) {
          buttons[0].addEventListener('click', this.close);
        }
        this.setStatus(Dialog.STATUS_OPEN);
      }, 10);
    });
  }

  public close = (result?: any) => {
    const options = this.options;
    if (this.card) {
      this.card.classList.add('close');
    }
    if (this.toolkitWrapper) {
      this.toolkitWrapper.style.opacity = '0';
    }

    unmountComponentAtNode(this.toolkitWrapper as HTMLElement);

    this.removeDialog();
    this.showPageScroll();
    this.setStatus(Dialog.STATUS_CLOSE);

    if (options.onClose) options.onClose(result);
    if (options.resolveOnClose) this.resolve(result);
  }

  public resolve(data: any) {
    if (this.options.onResolve) {
      this.options.onResolve(data);
    }
  }

  public isOpen = () => {
    return this.status === Dialog.STATUS_OPEN;
  }

  protected setStatus(status: string) {
    this.status = status;
  }

  private hiddenPageScroll() {
    this.html.style.overflow = 'hidden';
  }

  private showPageScroll() {
    this.html.style.overflow = 'auto';
  }

  private removeDialog = () => {
    setTimeout(() => {
      if (this.toolkitWrapper) {
        try {
          this.body.removeChild(this.toolkitWrapper);
        }
        catch (e) { }
      }
    }, 400);
  }

  private getResponsiveClasses = () => {
    switch (this.options.size) {
      case 'large':
        return ['col-lg-6', 'col-md-8', 'col-11'];
      case 'small':
      default:
        return ['col-lg-4', 'col-md-5', 'col-11'];
    }
  }

}
